import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// eslint-disable-next-line no-unused-vars
import { Plus, Edit2, Trash2, Calendar, MapPin, Users, Star, CheckCircle, Copy, ExternalLink, Download, RefreshCw, Link, Archive, Image as ImageIcon } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import ConfirmModal from '../components/common/ConfirmModal';
import EventActionsModal from '../components/events/EventActionsModal';
import EventManagementModal from '../components/events/EventManagementModal';
import ShareEventLinkModal from '../components/events/ShareEventLinkModal';
import { useNavigate } from 'react-router-dom';
import { eventAPI, attendeeAPI, registrationLinkAPI, shortUrlAPI } from '../api/endpoints';
import api from '../api/axios';
import { useToastContext } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useEvent } from '../context/EventContext';
import QRCode from 'qrcode';

const Events = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToastContext();
  const { permissions } = useAuth();
  const { setSelectedEvent: setGlobalSelectedEvent } = useEvent();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showResetLinkModal, setShowResetLinkModal] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'current', 'past'

  // Check user permissions
  const canCreateEvents = permissions?.canCreateEvents();
  const canEditEvents = permissions?.canEditEvents();

  // Fetch events with real-time attendee counts
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['events-with-counts', searchTerm],
    queryFn: async () => {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      console.log('🎆 Fetching events with attendee counts...');
      const response = await eventAPI.getAll(params);
      console.log('🎆 Events response:', response);
      const events = response.data.events || [];

      // Fetch attendee counts for all events in parallel
      const eventCountPromises = events.map(async (event) => {
        try {
          const attendeesResponse = await attendeeAPI.getAll(event.id, {});
          return {
            ...event,
            realAttendeeCount: attendeesResponse.data.attendees?.length || 0
          };
        } catch (error) {
          console.warn(`Failed to fetch attendees for event ${event.id}:`, error);
          return {
            ...event,
            realAttendeeCount: event.attendeeCount || 0
          };
        }
      });

      const eventsWithCounts = await Promise.all(eventCountPromises);

      return { events: eventsWithCounts };
    },
  });

  // Fetch default event
  const { data: defaultEventData } = useQuery({
    queryKey: ['defaultEvent'],
    queryFn: async () => {
      const response = await eventAPI.getDefault();
      return response.data;
    },
  });

  // Create event mutation
  const createMutation = useMutation({
    mutationFn: (data) => eventAPI.create(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      setSelectedEvent(null);
      toast.success('Event created successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create event');
    },
  });

  // Update event mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => eventAPI.update(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      setSelectedEvent(null);
      toast.success('Event updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update event');
    },
  });

  // Delete event mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => eventAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      queryClient.invalidateQueries({ queryKey: ['defaultEvent'] });
      setShowDeleteModal(false);
      setSelectedEvent(null);
      toast.success('Event deleted successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete event');
    },
  });

  // Duplicate event mutation
  const duplicateMutation = useMutation({
    mutationFn: (id) => eventAPI.duplicate(id),
    onSuccess: async () => {
       queryClient.invalidateQueries({ queryKey: ['events-with-counts'] });
      toast.success('Event duplicated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to duplicate event');
    },
  });

  // Set default event mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id) => eventAPI.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultEvent'] });
      toast.success('Default event set successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to set default event');
    },
  });

  // Unset default event mutation
  const unsetDefaultMutation = useMutation({
    mutationFn: () => eventAPI.unsetDefault(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['defaultEvent'] });
      toast.success('Default event removed successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove default event');
    },
  });

  // Reset registration link mutation
  const resetRegistrationLinkMutation = useMutation({
    mutationFn: (eventId) => registrationLinkAPI.resetLink(eventId),
    onSuccess: () => {
      toast.success('Registration link has been reset. Previous links are now invalid.');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reset registration link');
    },
  });

  // Create short URL mutation
  const createShortUrlMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: any }) => shortUrlAPI.create(eventId, data),
    onSuccess: () => {
      toast.success('Link copied successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create short URL');
    },
  });

  const handleFormSubmit = (formData) => {
    // Prevent multiple submissions
    if (createMutation.isPending || updateMutation.isPending) {
      return;
    }

    // If no end_date is provided, use start_date as end_date
    const submissionData = {
      ...formData,
      end_date: formData.end_date || formData.start_date || null
    };

    if (selectedEvent) {
      // Remove copy_fields_from_event_id from update requests
      const { copy_fields_from_event_id, ...updateData } = submissionData;
      updateMutation.mutate({ id: selectedEvent.id, data: updateData });
    } else {
      createMutation.mutate(submissionData);
    }
  };

  const handleDelete = () => {
    if (selectedEvent) {
      deleteMutation.mutate(selectedEvent.id);
    }
  };

  const handleEdit = (event) => {
    setGlobalSelectedEvent(event.id);
    navigate(`/event-settings#general`);
  };

  const handleCreate = () => {
    setGlobalSelectedEvent('new');
    navigate('/event-settings#general');
  };

  const handleSetDefault = (eventId) => {
    setDefaultMutation.mutate(eventId);
  };

  const handleUnsetDefault = () => {
    unsetDefaultMutation.mutate();
  };

  const handleToggleDefault = (eventId, isDefault) => {
    if (isDefault) {
      handleUnsetDefault();
    } else {
      handleSetDefault(eventId);
    }
  };

  const handleCopyRegistrationLink = async (event) => {
    try {
      const currentDomain = window.location.origin;
      const longUrl = `${currentDomain}/self-register?event=${event.slug || event.id}`;
      await navigator.clipboard.writeText(longUrl);
      toast.success(`Standard registration link copied for "${event.name}"!`);
    } catch (error) {
      toast.error('Failed to copy registration link.');
    }
  };

  const handleCopyShortLink = async (event) => {
    try {
      const origin = window.location.origin;
      const shortUrlsResponse = await shortUrlAPI.getAll(event.id);
      const rawData = shortUrlsResponse.data;
      const shortUrls: any[] = Array.isArray(rawData) 
        ? rawData 
        : (Array.isArray(rawData?.data) ? rawData.data : []);

      let urlToCopy = null;

      if (shortUrls && shortUrls.length > 0) {
        const activeShortUrl = shortUrls.find(url => url.is_active && !url.is_expired) || shortUrls[0];
        if (activeShortUrl) {
          const code = activeShortUrl.short_code;
          urlToCopy = code ? `${origin}/s/${code}` : activeShortUrl.short_url;
        }
      }

      if (!urlToCopy) {
        const createResponse = await createShortUrlMutation.mutateAsync({
          eventId: event.id,
          data: {}
        });
        const createdItem = createResponse.data?.data || createResponse.data;
        const code = createdItem?.short_code;
        urlToCopy = code ? `${origin}/s/${code}` : createdItem?.short_url;
      }

      if (urlToCopy) {
        await navigator.clipboard.writeText(urlToCopy);
        toast.success(`Shortened link copied: ${urlToCopy}`);
      } else {
        toast.error('Failed to resolve shortened link.');
      }
    } catch (error) {
      console.error('Copy short link error:', error);
      toast.error('Failed to copy short link.');
    }
  };

  const handleCopyUsherLink = async (event) => {
    try {
      const currentDomain = window.location.origin;
      const usherUrl = `${currentDomain}/self-register?event=${event.slug || event.id}&usher=true`;
      await navigator.clipboard.writeText(usherUrl);
      toast.success(`Usher link copied for "${event.name}"!`);
    } catch (error) {
      toast.error('Failed to copy usher link.');
    }
  };

  const handleDuplicate = (event) => {
    duplicateMutation.mutate(event.id);
  };

  // const handleResetRegistrationLink = (event) => {
  //   setSelectedEvent(event);
  //   setShowResetLinkModal(true);
  // };

  const confirmResetRegistrationLink = async () => {
    if (!selectedEvent) return;

    try {
      await resetRegistrationLinkMutation.mutateAsync(selectedEvent.id);
      setShowResetLinkModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Failed to reset registration link:', error);
    }
  };

  const handleDownloadEventQR = async (event) => {
    try {
      const response = await api.get(`events/${event.id}/download-qr-pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_registration_qr.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`QR code PDF downloaded for "${event.name}"!`);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    }
  };

  const handleDownloadQrPng = async (event) => {
    try {
      const currentDomain = window.location.origin;
      const registrationUrl = `${currentDomain}/self-register?event=${event.slug || event.id}`;

      // Generate QR code as data URL
      const qrCodeDataUrl = await QRCode.toDataURL(registrationUrl, {
        width: 1000, // High resolution for PNG download
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      const fileName = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_qr.png`;

      const link = document.createElement('a');
      link.href = qrCodeDataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`QR code PNG downloaded for "${event.name}"!`);

    } catch (error) {
      console.error('Failed to download QR PNG:', error);
      toast.error('Failed to download QR code. Please try again.');
    }
  };

  // Filter events based on active tab
  const filteredEvents = useMemo(() => {
    const allEvents = eventsData?.events || [];
    const now = new Date();

    switch (activeTab) {
      case 'current':
        return allEvents.filter(event => {
          if (!event.start_date) return true;
          const startDate = new Date(event.start_date);
          const endDate = event.end_date ? new Date(event.end_date) : startDate;
          const startOfRange = new Date(startDate);
          startOfRange.setHours(0, 0, 0, 0);
          const endOfRange = new Date(endDate);
          endOfRange.setHours(23, 59, 59, 999);
          return startOfRange <= now && endOfRange >= now;
        });
      case 'past':
        return allEvents.filter(event => {
          if (!event.end_date && !event.start_date) return false;
          const endDate = event.end_date ? new Date(event.end_date) : new Date(event.start_date);
          const endOfRange = new Date(endDate);
          endOfRange.setHours(23, 59, 59, 999);
          return endOfRange < now;
        });
      case 'upcoming':
        return allEvents.filter(event => {
          if (!event.start_date) return false;
          const startDate = new Date(event.start_date);
          const startOfRange = new Date(startDate);
          startOfRange.setHours(0, 0, 0, 0);
          return startOfRange > now;
        });
      default:
        return allEvents;
    }
  }, [eventsData, activeTab]);

  if (isLoading) {
    return (
      <Layout>
        <Loading fullScreen text="Loading events..." />
      </Layout>
    );
  }

  const events = eventsData?.events || [];
  const defaultEventId = defaultEventData?.data?.id || null;

  const tabs = [
    { id: 'all', label: 'All Events', count: events.length },
    {
      id: 'current', label: 'Current', count: filteredEvents.filter(e => {
        const now = new Date();
        if (!e.start_date) return false;
        const startDate = new Date(e.start_date);
        const endDate = e.end_date ? new Date(e.end_date) : startDate;
        return startDate <= now && endDate >= now;
      }).length
    },
    {
      id: 'upcoming', label: 'Upcoming', count: events.filter(e => {
        if (!e.start_date) return false;
        return new Date(e.start_date) > new Date();
      }).length
    },
    {
      id: 'past', label: 'Past', count: events.filter(e => {
        if (!e.end_date && !e.start_date) return false;
        const endDate = e.end_date ? new Date(e.end_date) : new Date(e.start_date);
        return endDate < new Date();
      }).length
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Events</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage your conference events</p>
          </div>
          {canCreateEvents && (
            <Button
              onClick={handleCreate}
              className="gap-2 w-full sm:w-auto"
            >
              <Plus className="h-5 w-5" />
              <span className="hidden xs:inline">Create Event</span>
              <span className="xs:hidden">Create</span>
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Card>
          <Card.Content className="p-0">
            <div className="flex border-b overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-0 px-3 sm:px-6 py-3 text-xs sm:text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === tab.id
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600'
                    }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </Card.Content>
        </Card>

        {/* Search */}
        <Card>
          <Card.Content>
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            />
          </Card.Content>
        </Card>

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredEvents.map((event) => {
            const isCurrentEvent = () => {
              if (!event.start_date) return false;
              const now = new Date();
              const startDate = new Date(event.start_date);
              const endDate = event.end_date ? new Date(event.end_date) : startDate;
              return startDate <= now && endDate >= now;
            };

            const isDefault = event.id === defaultEventId;

            return (
              <Card
                key={event.id}
                className={`hover:shadow-lg transition-all duration-200 ${isCurrentEvent() ? 'ring-2 ring-primary-500 shadow-md' : ''
                  }`}
              >
                <Card.Content className="p-5">
                  <div className="space-y-4">
                    {/* Header Section */}
                    <div className="flex flex-col gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isCurrentEvent() && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-primary-100 text-primary-700 border border-primary-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Live
                          </span>
                        )}
                        {isDefault && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Default
                          </span>
                        )}
                        {event.is_active ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                            Inactive
                          </span>
                        )}
                        <button
                          onClick={() => handleToggleDefault(event.id, isDefault)}
                          disabled={setDefaultMutation.isPending || unsetDefaultMutation.isPending}
                          className={`ml-auto p-1.5 rounded-full transition-all duration-200 ${isDefault
                            ? 'text-yellow-500 bg-yellow-50 hover:bg-yellow-100 hover:text-yellow-600'
                            : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'
                            }`}
                          title={isDefault ? 'Click to remove as default' : 'Click to set as default'}
                        >
                          <Star className={`h-4 w-4 transition-all ${isDefault ? 'fill-current' : ''}`} />
                        </button>
                      </div>

                      <h3 className="text-base font-semibold text-gray-900 truncate max-w-full" title={event.name}>
                        {event.name}
                      </h3>
                      
                      {event.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed mt-1 whitespace-pre-line">
                          {event.description}
                        </p>
                      )}
                    </div>

                    {/* Details Section */}
                    <div className="space-y-2.5 py-2">
                      {event.start_date && (
                        <div className="flex items-center gap-3 text-sm text-gray-700">
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary-50">
                            <Calendar className="h-4 w-4 text-primary-600" />
                          </div>
                          <span className="font-medium">
                            {new Date(event.start_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}`}
                          </span>
                        </div>
                      )}
                      {event.location && (
                        <div className="flex items-center gap-3 text-sm text-gray-700">
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-50">
                            <MapPin className="h-4 w-4 text-purple-600" />
                          </div>
                          <span className="font-medium line-clamp-1">{event.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-sm text-gray-700">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <span className="font-medium">
                          {(() => {
                            const count = event.realAttendeeCount !== undefined ? event.realAttendeeCount : (event.attendeeCount || 0);
                            return `${count} ${count === 1 ? 'attendee' : 'attendees'}`;
                          })()}
                        </span>
                      </div>
                    </div>

                    {/* Actions Section */}
                    <div className="pt-4 border-t border-gray-100">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full font-medium gap-2 justify-center"
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowActionsModal(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                        Manage Event
                      </Button>
                    </div>
                  </div>
                </Card.Content>
              </Card>
            );
          })}
        </div>

        {filteredEvents.length === 0 && (
          <Card>
            <Card.Content className="text-center py-12">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {activeTab === 'all' ? 'No Events Found' : `No ${activeTab} Events`}
              </h3>
              <p className="text-gray-600 mb-4">
                {activeTab === 'all'
                  ? 'Create your first event to get started'
                  : `There are no ${activeTab} events at the moment`
                }
              </p>
              {activeTab === 'all' && canCreateEvents && (
                <Button onClick={handleCreate} className="gap-2">
                  <Plus className="h-5 w-5" />
                  Create Event
                </Button>
              )}
            </Card.Content>
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedEvent(null);
          }}
          onConfirm={handleDelete}
          title="Delete Event"
          message={`Are you sure you want to delete "${selectedEvent?.name}"? This will also delete all associated attendees and data.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          loading={deleteMutation.isPending}
        />

        {/* Event Actions Modal */}
        <EventActionsModal
          isOpen={showActionsModal}
          onClose={() => {
            setShowActionsModal(false);
          }}
          event={selectedEvent}
          onCopyRegistrationLink={handleCopyRegistrationLink}
          onCopyShortLink={handleCopyShortLink}
          onOpenShareModal={(event) => {
            setSelectedEvent(event);
            setShowShareModal(true);
          }}
          onCopyUsherLink={handleCopyUsherLink}
          onDownloadQrPdf={handleDownloadEventQR}
          onDownloadQrPng={handleDownloadQrPng}
          onDuplicate={handleDuplicate}
          onSettings={(event) => {
            setGlobalSelectedEvent(event.id);
            navigate('/event-settings');
          }}
          onEdit={handleEdit}
          onDelete={(event) => {
            setSelectedEvent(event);
            setShowDeleteModal(true);
          }}
        />

        {/* Share Event Link Modal (Google Forms / Bitly Style Shortener) */}
        <ShareEventLinkModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
          }}
          event={selectedEvent}
          onDownloadQrPdf={handleDownloadEventQR}
          onDownloadQrPng={handleDownloadQrPng}
        />

        {/* Reset Registration Link Confirmation Modal */}
        <ConfirmModal
          isOpen={showResetLinkModal}
          onClose={() => {
            setShowResetLinkModal(false);
            setSelectedEvent(null);
          }}
          onConfirm={confirmResetRegistrationLink}
          title="Reset Registration Link"
          message={
            (<div className="space-y-4">
              <p>
                Are you sure you want to reset the registration link for <strong>"{selectedEvent?.name}"</strong>?
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  What will happen:
                </h4>
                <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                  <li>A new registration token will be generated</li>
                  <li>All existing registration links will become invalid</li>
                  <li>All existing short URLs will become invalid</li>
                  <li>QR codes with old links will stop working</li>
                  <li>You'll need to share the new registration link</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">
                  When to reset:
                </h4>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>Registration link has been compromised or shared inappropriately</li>
                  <li>You want to stop registrations temporarily and restart with a new link</li>
                  <li>Security concerns about who has access to the current link</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                <strong>Note:</strong> This action cannot be undone. Make sure you're ready to distribute new registration links.
              </p>
            </div>) as any
          }
          confirmText="Reset Link"
          cancelText="Cancel"
          variant="warning"
          loading={resetRegistrationLinkMutation.isPending}
        />

        {/* Event Management Modal */}
        <EventManagementModal
          event={selectedEvent}
          isOpen={showManagementModal}
          onClose={() => {
            setShowManagementModal(false);
            setSelectedEvent(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default Events;
